import { useEffect, useState } from 'react';
import api from '../../api';
import { QUALITY_SERVICE_API_URL } from '../../config';
import { toUtcEndOfDay, toUtcMidnight } from '../../utils/dateFormat';
import { STATUS_COLORS } from './colors';

const FEEDBACK_COLORS = {
  accurate: '#16a34a',
  not_accurate: '#dc2626',
};

const FEEDBACK_LABELS = {
  accurate: 'Accurate',
  not_accurate: 'Not accurate',
};

const PAGE_SIZE = 10;

function FeedbackPill({ feedback }) {
  const color = FEEDBACK_COLORS[feedback] || '#6b7280';
  const label = FEEDBACK_LABELS[feedback] || feedback;
  return <span className="sense-shield-pill" style={{ background: color }}>{label}</span>;
}

function ResultPill({ status }) {
  const color = STATUS_COLORS[(status || '').toLowerCase()] || '#6b7280';
  return <span className="sense-shield-pill" style={{ background: color }}>{status}</span>;
}

function FeedbackTable({ dateRange }) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set('from', toUtcMidnight(dateRange.startDate).toISOString());
    params.set('to', toUtcEndOfDay(dateRange.endDate).toISOString());
    params.set('page', page.toString());
    params.set('pageSize', PAGE_SIZE.toString());

    api.get(`${QUALITY_SERVICE_API_URL}/hub/sense-shield/feedback-paginated?${params.toString()}`)
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [dateRange, page]);

  if (loading || !data) {
    return (
      <div>
        <div className="sense-shield-panel-title">Reviewer Feedback</div>
        <div className="sense-shield-panel-content">
          <div className="sense-shield-empty">{loading ? 'Loading...' : 'No data available'}</div>
        </div>
      </div>
    );
  }

  const { data: rows, page: currentPage, total, totalPages } = data;

  return (
    <div>
      <div className="sense-shield-panel-title">Reviewer Feedback</div>
      <div className="sense-shield-panel-content sense-shield-table-wrapper">
        <table className="sense-shield-table">
          <thead>
            <tr>
              <th>Prompt ID</th>
              <th>Run ID</th>
              <th>Result</th>
              <th>Feedback</th>
              <th>Reviewer</th>
              <th>Comment</th>
              <th>Submitted At</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{row.promptId}</td>
                <td>{row.runId}</td>
                <td><ResultPill status={row.resultStatus} /></td>
                <td><FeedbackPill feedback={row.feedback} /></td>
                <td>{row.slackUsername}</td>
                <td className="sense-shield-cell-wrap" title={row.comment || ''}>{row.comment || '—'}</td>
                <td>{new Date(row.createdAt).toLocaleString()}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="sense-shield-empty">No reviewer feedback for the selected date range</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {total > 0 && (
        <div className="sense-shield-pagination">
          <span className="sense-shield-pagination-range">
            {(currentPage - 1) * PAGE_SIZE + 1}-{Math.min(currentPage * PAGE_SIZE, total)} of {total}
          </span>
          <div className="sense-shield-pagination-controls">
            <button
              type="button"
              className="sense-shield-pagination-nav-btn"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              ← Prev
            </button>
            <span className="sense-shield-pagination-page-badge">{currentPage}</span>
            <button
              type="button"
              className="sense-shield-pagination-nav-btn"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default FeedbackTable;
